import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

export interface TourDiscount {
  id: string;
  tour_id: string | null;
  type: 'percent' | 'fixed';
  value: number;
  active: boolean;
  expires_at: string | null;
  created_at: string;
}

export interface TourDiscountInput {
  type: 'percent' | 'fixed';
  value: number;
  active: boolean;
  tour_id: string | null; // null = applies to all tours
  expires_at: string | null; // null = no expiry (YYYY-MM-DD)
}

export interface ResolvedPrice {
  originalPrice: number;
  finalPrice: number;
  hasDiscount: boolean;
}

export function useTourDiscounts() {
  const [discounts, setDiscounts] = useState<TourDiscount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDiscounts = async (showLoading = false) => {
    if (showLoading) setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('vietnamtravel_tour_discounts')
        .select('*')
        .order('created_at', { ascending: false });
      if (err) throw err;
      setDiscounts((data || []) as TourDiscount[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load tour discounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDiscounts(true);
  }, []);

  const createDiscount = async (input: TourDiscountInput) => {
    const { error: err } = await supabase.from('vietnamtravel_tour_discounts').insert(input);
    if (err) return { error: err.message };
    await fetchDiscounts();
    return { error: null };
  };

  const updateDiscount = async (id: string, input: Partial<TourDiscountInput>) => {
    // If the expiry date is being changed, reset the "already notified" flag so the
    // expiry reminder can fire again for the new date.
    const patch = { ...input } as Record<string, unknown>;
    if (Object.prototype.hasOwnProperty.call(input, 'expires_at')) {
      patch.expiry_notified = false;
    }
    const { error: err } = await supabase.from('vietnamtravel_tour_discounts').update(patch).eq('id', id);
    if (err) return { error: err.message };
    await fetchDiscounts();
    return { error: null };
  };

  const toggleActive = async (id: string, active: boolean) => {
    const { error: err } = await supabase.from('vietnamtravel_tour_discounts').update({ active }).eq('id', id);
    if (err) return { error: err.message };
    await fetchDiscounts();
    return { error: null };
  };

  const deleteDiscount = async (id: string) => {
    const { error: err } = await supabase.from('vietnamtravel_tour_discounts').delete().eq('id', id);
    if (err) return { error: err.message };
    await fetchDiscounts();
    return { error: null };
  };

  return {
    discounts,
    loading,
    error,
    refresh: fetchDiscounts,
    createDiscount,
    updateDiscount,
    toggleActive,
    deleteDiscount,
  };
}

// Whether a discount is currently valid: it must be active AND not past its expiry date.
export function isDiscountExpired(discount: TourDiscount): boolean {
  if (!discount.expires_at) return false;
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(
    today.getDate(),
  ).padStart(2, '0')}`;
  return discount.expires_at < todayStr;
}

// Resolve the active discount for a given tour. A tour-specific discount takes
// priority over a global "all tours" discount.
export function resolveTourDiscount(
  tourId: string | null | undefined,
  discounts: TourDiscount[],
): TourDiscount | null {
  const active = discounts.filter((d) => d.active && !isDiscountExpired(d));
  if (tourId) {
    const specific = active.find((d) => d.tour_id === tourId);
    if (specific) return specific;
  }
  return active.find((d) => d.tour_id === null) || null;
}

// Apply a discount to a base price, returning the original and the final price.
export function applyTourDiscount(
  basePrice: number,
  discount: TourDiscount | null,
): ResolvedPrice {
  if (!discount) {
    return { originalPrice: basePrice, finalPrice: basePrice, hasDiscount: false };
  }
  const value = Number(discount.value) || 0;
  let final = basePrice;
  if (discount.type === 'percent') {
    const pct = Math.min(Math.max(value, 0), 100);
    final = basePrice * (1 - pct / 100);
  } else {
    final = basePrice - value;
  }
  final = Math.max(0, Math.round(final * 100) / 100);
  return { originalPrice: basePrice, finalPrice: final, hasDiscount: final < basePrice };
}